<template>
  <div>
    <div v-if="hasRows" class="table-responsive">
      <table class="table table-sm table-bordered align-middle mb-0">
        <thead class="table-light">
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
              :class="headerClass(column.key)"
            >
              {{ column.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in rows" :key="index">
            <td class="align-top">
              <div class="fw-semibold">{{ row.description }}</div>
            </td>
            <td class="text-center align-top">
              <span class="badge bg-info">
                {{ row.quantity.display }}
              </span>
            </td>
            <td class="text-end align-top">
              <div class="fw-semibold">{{ row.unitPrice.displayGross }}</div>
            </td>
            <td class="text-end align-top">
              <div class="fw-semibold">{{ formatTaxRate(row.tax) }}</div>
            </td>
            <td class="text-end align-top">
              <div class="fw-semibold">{{ formatTaxAmount(row.tax) }}</div>
            </td>
            <td class="text-end align-top">
              <span class="fw-bold">{{ row.total.display }}</span>
            </td>
          </tr>
        </tbody>
        <tfoot class="table-light">
          <tr>
            <td :colspan="summaryColspan" class="text-end fw-bold">Gesamtsumme Netto:</td>
            <td class="text-end fw-bold">{{ summary.display.net }}</td>
          </tr>
          <tr
            v-for="breakdown in taxBreakdown"
            :key="`${breakdown.taxRateId || breakdown.rate}-${breakdown.displayName || 'rate'}`"
          >
            <td :colspan="summaryColspan" class="text-end small text-muted">
              {{ breakdown.displayName || `${breakdown.rate}%` }}:
            </td>
            <td class="text-end small text-muted">{{ breakdown.display.tax }}</td>
          </tr>
          <tr>
            <td :colspan="summaryColspan" class="text-end fw-bold">Gesamtsumme:</td>
            <td class="text-end fw-bold">{{ summary.display.gross }}</td>
          </tr>
        </tfoot>
      </table>
    </div>
    <div v-else class="text-muted fst-italic small">
      {{ emptyLabel }}
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  tableData: {
    type: Object,
    required: true,
  },
  emptyLabel: {
    type: String,
    default: 'Keine Einträge vorhanden',
  },
})

const rows = computed(() => props.tableData?.rows || [])
const hasRows = computed(() => rows.value.length > 0)
const columns = computed(() => props.tableData?.columns || [])
const summary = computed(() => props.tableData?.summary || { display: { net: '0.00€', tax: '0.00€', gross: '0.00€' } })
const taxBreakdown = computed(() => props.tableData?.taxBreakdown || [])
const currencySymbol = computed(() => props.tableData?.currencySymbol || '€')
const summaryColspan = computed(() => Math.max((columns.value.length || 1) - 1, 1))

const headerClass = (key) => {
  switch (key) {
    case 'description':
      return 'text-start'
    case 'quantity':
      return 'text-center'
    default:
      return 'text-end'
  }
}

const formatTaxRate = (tax) => {
  if (!tax) return ''
  const displayName = typeof tax.displayName === 'string' ? tax.displayName.trim() : ''
  if (displayName) {
    return displayName
  }
  const numericRate = Number.parseFloat(tax.rate)
  if (Number.isFinite(numericRate) && numericRate > 0) {
    return `${numericRate}%`
  }
  return ''
}

const formatTaxAmount = (tax) => {
  if (!tax) return ''
  const displayAmount = typeof tax.displayAmount === 'string' ? tax.displayAmount.trim() : ''
  if (displayAmount) {
    return displayAmount
  }
  const numericAmount = Number.parseFloat(tax.amount)
  if (!Number.isFinite(numericAmount) || Math.abs(numericAmount) < 1e-6) {
    return ''
  }
  return `${numericAmount.toFixed(2)}${currencySymbol.value}`
}
</script>
